import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Home,
  Search,
  Bookmark,
  Settings,
  Film,
  User,
  BookOpen,
  Plus,
  ArrowLeft,
  Menu,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import EnhancedFeed from "./EnhancedFeed";
import StoriesBar from "./StoriesBar";
import StoryViewer from "./StoryViewer";
import ProfilePage from "./ProfilePage";
import UserProfile from "./UserProfile";
import UserProfileNew from "./UserProfileNew";
import PDFUploadModal from "./pdf/PDFUploadModal";
import ReelsPage from "./ReelsPage";
import DiscoverGrid from "./DiscoverGrid";
import SettingsScreen from "./SettingsScreen";
import ChaptersTab from "./ChaptersTab";
import SaveToCollectionModal from "./SaveToCollectionModal";
import InstagramCarousel from "./InstagramCarousel";
import {
  getAuthHeader,
  getToken as getStoredToken,
} from "../services/authToken";
import useInstanceStore from "@/stores/instanceStore";
import { useApiEffect } from "@/hooks/useApi";
import {
  instancesAPI,
  feedAPI,
  interactionsAPI,
  discoverAPI,
} from "@/services/api";
import { toast } from "sonner";
import Header from "./Header";
import useUploadLimit from "@/hooks/useUploadLimit";
import UploadLimitModal from "./pdf/UploadLimitModal";
import { useTranslation } from "react-i18next";
import { logger } from "@/utils/logger";

// Force rebuild
const DISCOVER_VIEWER_PAGE_SIZE = 24;

const MainLayout = ({ currentUser, initialInstance, onResetOnboarding }) => {
  const { t } = useTranslation();
  
  const SLIDE_TYPE_OPTIONS = [
    { value: "all", label: t('filters.all_slide_types') },
    { value: "concept", label: t('filters.concept') },
    { value: "quiz", label: t('filters.quiz') },
    { value: "mindmap", label: t('filters.mindmap') },
    { value: "extra", label: t('filters.extra') },
  ];

  const LANGUAGE_OPTIONS = [
    { value: "all", label: t('filters.all_languages') },
    { value: "en", label: t('filters.english') },
    { value: "it", label: t('filters.italiano') },
  ];

  const DIFFICULTY_OPTIONS = [
    { value: "all", label: t('filters.all_difficulties') },
    { value: "beginner", label: t('filters.beginner') },
    { value: "intermediate", label: t('filters.intermediate') },
    { value: "advanced", label: t('filters.advanced') },
  ];

  const { selectedInstanceId, instances, setInstances, getSelectedInstance } =
    useInstanceStore();

  // Debug logging
  logger.log("🔍 MainLayout: Zustand state:", {
    selectedInstanceId,
    instancesCount: instances.length,
    instances: instances.map((i) => ({ id: i.id, name: i.name })),
  });

  const [currentTab, setCurrentTab] = useState("feed");
  const [savedPosts, setSavedPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [currentStoryEntity, setCurrentStoryEntity] = useState(null);
  const [currentStoryData, setCurrentStoryData] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // Move settings state to MainLayout
  const [viewedStories, setViewedStories] = useState([]); // Track viewed stories
  const [selectedPost, setSelectedPost] = useState(null); // For post viewer (search/discover)
  const [savedPostsViewMode, setSavedPostsViewMode] = useState("grid"); // 'grid' or 'list'
  const [savedScrollTargetId, setSavedScrollTargetId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false); // PDF upload modal state
  const [savedCollections, setSavedCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [showSaveToCollection, setShowSaveToCollection] = useState(false);
  const [postToSave, setPostToSave] = useState(null);
  const [postCurrentCollections, setPostCurrentCollections] = useState([]);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [discoverFilters, setDiscoverFilters] = useState({
    instance: "all",
    slideType: "all",
    language: "all",
    difficulty: "all",
  });
  const [discoverPosts, setDiscoverPosts] = useState([]); // All discover posts
  const [discoverScrollTargetId, setDiscoverScrollTargetId] = useState(null); // Post to scroll to in viewer
  const [showDiscoverViewer, setShowDiscoverViewer] = useState(false);
  const [discoverViewerSource, setDiscoverViewerSource] = useState("discover"); // 'discover' | 'search'
  const [discoverViewerQuery, setDiscoverViewerQuery] = useState("");
  const [discoverViewerFilters, setDiscoverViewerFilters] =
    useState(discoverFilters);
  const [discoverViewerOffset, setDiscoverViewerOffset] = useState(0);
  const [discoverViewerHasMore, setDiscoverViewerHasMore] = useState(false);
  const [discoverViewerLoading, setDiscoverViewerLoading] = useState(false);
  const [discoverViewerError, setDiscoverViewerError] = useState(null);
  const discoverViewerRef = useRef(null);

  // Upload limit check state
  const { checkLimit, limitStatus, loading: limitLoading, resetStatus } = useUploadLimit();
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Get current instance from Zustand store
  const currentInstance = getSelectedInstance();

  useEffect(() => {
    setDiscoverFilters((prev) => ({
      ...prev,
      instance: "all",
    }));
  }, [currentInstance?.id]);

  const filterControls = (
    <div className="flex flex-wrap gap-2 items-center text-xs text-gray-600 mb-4">
      <Select
        value={discoverFilters.instance}
        onValueChange={(value) =>
          setDiscoverFilters((prev) => ({ ...prev, instance: value }))
        }
      >
        <SelectTrigger className="w-[160px] h-8 text-xs">
          <SelectValue placeholder="All books" />
        </SelectTrigger>
        <SelectContent
          position="popper"
          className="max-h-[200px] overflow-y-auto"
          sideOffset={4}
        >
          <SelectItem value="all">All books</SelectItem>
          {instances.map((instance) => (
            <SelectItem key={instance.id} value={instance.id}>
              {instance.name || "Untitled"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={discoverFilters.slideType}
        onValueChange={(value) =>
          setDiscoverFilters((prev) => ({ ...prev, slideType: value }))
        }
      >
        <SelectTrigger className="w-[140px] h-8 text-xs">
          <SelectValue placeholder={t('filters.all_slide_types')} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          className="max-h-[200px] overflow-y-auto"
          sideOffset={4}
        >
          {SLIDE_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={discoverFilters.language}
        onValueChange={(value) =>
          setDiscoverFilters((prev) => ({ ...prev, language: value }))
        }
      >
        <SelectTrigger className="w-[130px] h-8 text-xs">
          <SelectValue placeholder={t('filters.all_languages')} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          className="max-h-[200px] overflow-y-auto"
          sideOffset={4}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={discoverFilters.difficulty}
        onValueChange={(value) =>
          setDiscoverFilters((prev) => ({ ...prev, difficulty: value }))
        }
      >
        <SelectTrigger className="w-[140px] h-8 text-xs">
          <SelectValue placeholder={t('filters.all_difficulties')} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          className="max-h-[200px] overflow-y-auto"
          sideOffset={4}
        >
          {DIFFICULTY_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // Load instances into Zustand store - optimized to prevent duplicate calls
  useEffect(() => {
    const loadInstancesIntoStore = async () => {
      // Skip if instances already loaded
      if (instances.length > 0) {
        logger.log("🔍 MainLayout: Instances already loaded, skipping fetch");
        return;
      }

      try {
        logger.log("🔍 MainLayout: Loading instances into Zustand store...");
        const instanceData = await instancesAPI.getAll();
        logger.log("🔍 MainLayout: Instance API response:", instanceData);

        if (instanceData && instanceData.instances) {
          logger.log(
            "✅ MainLayout: Setting instances in store:",
            instanceData.instances.length
          );
          setInstances(instanceData.instances);
        } else {
          logger.warn(
            "⚠️ MainLayout: No instances data received, trying fallback"
          );
          // Single fallback attempt only
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const token = getStoredToken();

          const response = await fetch(`${backendUrl}/api/instances`, {
            headers: getAuthHeader(),
          });

          if (response.ok) {
            const fallbackData = await response.json();
            logger.log(
              "✅ MainLayout: Fallback fetch success:",
              fallbackData.instances?.length || 0
            );
            if (fallbackData.instances) {
              setInstances(fallbackData.instances);
            }
          } else {
            throw new Error(`Fallback fetch failed: ${response.status}`);
          }
        }
      } catch (error) {
        logger.error("❌ MainLayout: Error loading instances:", error);
        // Don't try multiple fallbacks - just log and continue
      }
    };

    loadInstancesIntoStore();
  }, [setInstances, instances.length]);

  // Handle New/Upload button click - check limit first
  const handleNewUploadClick = async () => {
    logger.log('🔄 MainLayout: Checking upload limit before showing modal');
    await checkLimit();
    setShowLimitModal(true);
  };

  // Handle proceed from limit modal - user can upload
  const handleProceedToUpload = () => {
    setShowLimitModal(false);
    resetStatus();
    setShowUploadModal(true);
  };

  // Handle close limit modal without proceeding
  const handleCloseLimitModal = () => {
    setShowLimitModal(false);
    resetStatus();
  };

  const handleUploadSuccess = (uploadData) => {
    logger.log("PDF upload successful:", uploadData);

    // Close the upload modal
    setShowUploadModal(false);

    // Immediately add the new instance to the store
    if (uploadData && uploadData.instance) {
      const newInstance = {
        ...uploadData.instance,
        status: "ready",
        posts_count: 0,
        progress: 0,
        processing_stage: null,
      };

      // Add to instances array immediately for instant UI update
      const currentInstances = useInstanceStore.getState().instances;
      const updatedInstances = [...currentInstances, newInstance];
      setInstances(updatedInstances);

      // Select the new instance
      useInstanceStore.getState().setSelectedInstanceId(newInstance.id);

      // Optional: Also refresh from API in the background to ensure sync
      setTimeout(async () => {
        try {
          const instanceData = await instancesAPI.getAll();
          if (instanceData && instanceData.instances) {
            setInstances(instanceData.instances);
          }
        } catch (error) {
          logger.error("Background refresh failed:", error);
        }
      }, 2000);
    }

    // Switch to profile tab to show the new instance card
    setCurrentTab("profile");
  };

  // Load entities and stories for current instance
  const { data: entities, loading: loadingEntities } = useApiEffect(
    () =>
      currentInstance?.id
        ? instancesAPI.getEntities(currentInstance.id)
        : Promise.resolve([]),
    [currentInstance?.id]
  );

  const { data: stories, loading: loadingStories } = useApiEffect(
    () =>
      currentInstance?.id
        ? instancesAPI.getStories(currentInstance.id)
        : Promise.resolve([]),
    [currentInstance?.id]
  );

  // Load saved posts when instance changes
  useEffect(() => {
    if (currentInstance?.id) {
      loadSavedPosts();
    }
  }, [currentInstance?.id]);

  // Load collections on mount
  useEffect(() => {
    loadCollections();
  }, []);

  // Load saved posts when Saved tab is opened
  useEffect(() => {
    if (currentTab === "saved") {
      loadSavedPosts(currentCollection);
    }
  }, [currentTab, currentCollection]);

  // Scroll to target post when switching to list view from grid
  useEffect(() => {
    if (savedPostsViewMode === "list" && savedScrollTargetId) {
      const timer = setTimeout(() => {
        const element = document.querySelector(
          `[data-post-id="${savedScrollTargetId}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
          setSavedScrollTargetId(null);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [savedPostsViewMode, savedScrollTargetId]);

  // Scroll to target post when switching to discover feed view
  useEffect(() => {
    if (!showDiscoverViewer || !discoverScrollTargetId) {
      return;
    }

    const timer = setTimeout(() => {
      const container = discoverViewerRef.current;
      if (!container) {
        setDiscoverScrollTargetId(null);
        return;
      }

      const target = container.querySelector(
        `[data-post-id="${discoverScrollTargetId}"]`
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setDiscoverScrollTargetId(null);
    }, 100);

    return () => clearTimeout(timer);
  }, [showDiscoverViewer, discoverScrollTargetId]);

  const loadSavedPosts = async (collectionName = null) => {
    try {
      const { posts } = await interactionsAPI.getSaved({
        instanceId: currentInstance?.id,
        collection: collectionName,
        includeCollections: true,
      });
      setSavedPosts(posts);
    } catch (error) {
      logger.error("Error loading saved posts:", error);
      toast.error("Failed to load saved posts");
    }
  };

  const loadCollections = async () => {
    setLoadingCollections(true);
    try {
      const collections = await interactionsAPI.getCollections();
      logger.log("Loaded collections:", collections);
      setSavedCollections(collections);
    } catch (error) {
      logger.error("Error loading collections:", error);
      toast.error("Failed to load collections");
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleCreateCollection = async (name) => {
    try {
      await interactionsAPI.createCollection(name);

      await loadCollections(); // Refresh list
      return true;
    } catch (error) {
      logger.error("Error creating collection:", error);
      toast.error("Failed to create collection");
      throw error;
    }
  };

  const handleRenameCollection = async (currentName) => {
    const proposedName = prompt("Enter new collection name:", currentName);
    if (
      !proposedName ||
      !proposedName.trim() ||
      proposedName.trim() === currentName
    ) {
      return;
    }

    const trimmedName = proposedName.trim();

    try {
      await interactionsAPI.renameCollection(currentName, trimmedName);
      await loadCollections();
      if (currentCollection === currentName) {
        setCurrentCollection(trimmedName);
      }
      toast.success("Collection renamed");
    } catch (error) {
      logger.error("Error renaming collection:", error);
      toast.error("Failed to rename collection");
    }
  };

  const handleDeleteCollection = async (name) => {
    const confirmed = window.confirm(`Delete collection "${name}"?`);
    if (!confirmed) return;

    try {
      await interactionsAPI.deleteCollection(name);
      await loadCollections();
      if (currentCollection === name) {
        setCurrentCollection(null);
        await loadSavedPosts(null);
      }
      toast.success("Collection deleted");
    } catch (error) {
      logger.error("Error deleting collection:", error);
      toast.error("Failed to delete collection");
    }
  };

  const handleSaveToCollections = async (selectedCollectionNames) => {
    if (!postToSave) return;

    try {
      // Add to selected collections
      await interactionsAPI.addToCollections(
        postToSave.id,
        selectedCollectionNames
      );

      // Remove from unselected collections (if post was already saved)
      const currentCollections = postCurrentCollections || [];
      const toRemove = currentCollections.filter(
        (c) => !selectedCollectionNames.includes(c)
      );
      if (toRemove.length > 0) {
        await interactionsAPI.removeFromCollections(postToSave.id, toRemove);
      }

      // Refresh data
      await loadSavedPosts(currentCollection);
      await loadCollections();

      // Update UI
      setShowSaveToCollection(false);
      setPostToSave(null);
      setPostCurrentCollections([]);

      if (selectedPost && selectedPost.id === postToSave.id) {
        setSelectedPost((prev) =>
          prev
            ? {
                ...prev,
                saved_by_user: true,
                collections: selectedCollectionNames,
              }
            : null
        );
      }

      toast.success(`Saved to ${selectedCollectionNames.length} collection(s)`);
    } catch (error) {
      logger.error("Error saving to collections:", error);
      toast.error("Failed to save to collections");
    }
  };

  const handleRemoveFromAllCollections = async () => {
    if (!postToSave) return;

    try {
      await interactionsAPI.removeFromAllCollections(postToSave.id);

      // Refresh data
      await loadSavedPosts(currentCollection);
      await loadCollections();

      // Update UI
      setShowSaveToCollection(false);
      setPostToSave(null);
      setPostCurrentCollections([]);

      if (selectedPost && selectedPost.id === postToSave.id) {
        setSelectedPost((prev) =>
          prev ? { ...prev, saved_by_user: false, collections: [] } : null
        );
      }

      toast.success("Removed from all collections");
    } catch (error) {
      logger.error("Error removing from collections:", error);
      toast.error("Failed to remove from collections");
    }
  };

  const handleOpenSaveModal = async (post) => {
    // Check if post is already saved and get collections
    try {
      const { posts } = await interactionsAPI.getSaved({
        includeCollections: true,
      });
      const savedPost = posts.find((p) => p.id === post.id);

      setPostToSave(post);
      setPostCurrentCollections(savedPost?.collections || []);
      setShowSaveToCollection(true);
    } catch (error) {
      // Fallback: open modal anyway
      logger.error("Error checking saved state:", error);
      setPostToSave(post);
      setPostCurrentCollections([]);
      setShowSaveToCollection(true);
    }
  };

  const handleInstanceChange = (instance) => {
    useInstanceStore.getState().setSelectedInstanceId(instance.id);
    if (instance.status === "processing") {
      toast.info(
        `${instance.name} is still processing. Some content may be limited.`
      );
    } else if (instance.status === "ready") {
      toast.success(`Switched to ${instance.name}`);
    }
  };

  // Helper function to map search results to post format (same as DiscoverGrid)
  const mapSearchResultToPost = useCallback((result) => {
    const metadata = result?.metadata || {};

    // Parse the content if it's a JSON string (carousel object or slides array)
    let parsedContent = null;
    let contentTitle = null;
    let contentBody = null;
    let slides = null;

    if (result.content) {
      if (typeof result.content === "string") {
        const contentString = result.content.trim();
        try {
          // Try to parse as JSON
          if (contentString.startsWith("{")) {
            // Carousel object: {"id":"carousel_...", "title":"...", "slides":[...]}
            parsedContent = JSON.parse(contentString);
            contentTitle = parsedContent.title;
            contentBody =
              parsedContent.slides?.[0]?.content ||
              parsedContent.slides?.[0]?.title;
            slides = parsedContent.slides;
          } else if (contentString.startsWith("[")) {
            // Slides array: [{"type":"concept", "title":"...", "content":"..."}]
            const slidesArray = JSON.parse(contentString);
            if (Array.isArray(slidesArray) && slidesArray.length > 0) {
              contentTitle = slidesArray[0].title;
              contentBody = slidesArray[0].content || slidesArray[0].title;
              slides = slidesArray;
            }
          } else {
            // Plain text content
            contentBody = contentString;
          }
        } catch (e) {
          // If parsing fails, use as plain text
          contentBody = contentString;
        }
      } else if (Array.isArray(result.content)) {
        const slidesArray = result.content;
        if (Array.isArray(slidesArray) && slidesArray.length > 0) {
          contentTitle = slidesArray[0].title;
          contentBody = slidesArray[0].content || slidesArray[0].title;
          slides = slidesArray;
        }
      } else if (typeof result.content === "object") {
        parsedContent = result.content;
        contentTitle = parsedContent?.title;
        const firstSlide = parsedContent?.slides?.[0];
        contentBody = firstSlide?.content || firstSlide?.title;
        slides = parsedContent?.slides;
      }
    }

    const titleFromMetadata = metadata.slide_title || metadata.title;
    const fallbackTitle =
      contentTitle ||
      (contentBody ? contentBody.substring(0, 60) : "Search result");

    return {
      id: result.id,
      title: titleFromMetadata || contentTitle || fallbackTitle,
      body: contentBody || result.content,
      content: contentBody || result.content,
      media_url: metadata.media_url,
      type: metadata.slide_type || "post",
      instance_id: metadata.instance_id,
      document_id: metadata.document_id,
      language: metadata.language,
      difficulty: metadata.difficulty,
      source_ref: metadata.source_ref,
      source_line_start: metadata.line_start ?? metadata.source_line_start,
      source_line_end: metadata.line_end ?? metadata.source_line_end,
      metadata,
      sourceAttribution: result.sourceAttribution,
      source: result.source,
      likes: metadata.likes || 0,
      comments: metadata.comments || 0,
      slides: slides, // Include slides if available
    };
  }, []);

  const handleDiscoverGridPostClick = useCallback(
    (post, allPosts) => {
      if (!post) return;

      logger.log("Grid post clicked:", post);

      const postsList =
        Array.isArray(allPosts) && allPosts.length ? allPosts : [post];
      const trimmedQuery = searchQuery.trim();

      setDiscoverPosts(postsList);
      setDiscoverScrollTargetId(post.id);
      setDiscoverViewerFilters(discoverFilters);
      setDiscoverViewerQuery(trimmedQuery);
      setDiscoverViewerSource(trimmedQuery ? "search" : "discover");
      setDiscoverViewerOffset(postsList.length);
      setDiscoverViewerHasMore(postsList.length > 0);
      setDiscoverViewerLoading(false);
      setDiscoverViewerError(null);
      setShowDiscoverViewer(true);
    },
    [discoverFilters, searchQuery]
  );

  const loadMoreDiscoverViewerPosts = useCallback(async () => {
    if (
      !showDiscoverViewer ||
      discoverViewerLoading ||
      !discoverViewerHasMore
    ) {
      return;
    }

    setDiscoverViewerLoading(true);
    setDiscoverViewerError(null);

    try {
      const response = await discoverAPI.search({
        query: discoverViewerSource === "search" ? discoverViewerQuery : "",
        limit: DISCOVER_VIEWER_PAGE_SIZE,
        offset: discoverViewerOffset,
        instances:
          discoverViewerFilters.instance === "all"
            ? []
            : [discoverViewerFilters.instance],
        slideTypes:
          discoverViewerFilters.slideType === "all"
            ? []
            : [discoverViewerFilters.slideType],
        languages:
          discoverViewerFilters.language === "all"
            ? []
            : [discoverViewerFilters.language],
        difficulties:
          discoverViewerFilters.difficulty === "all"
            ? []
            : [discoverViewerFilters.difficulty],
      });

      const results = response?.results || [];
      const mappedPosts = results.map(mapSearchResultToPost);

      setDiscoverViewerHasMore(
        Boolean(response?.metadata?.hasMore) && mappedPosts.length > 0
      );
      setDiscoverViewerOffset((prev) => prev + mappedPosts.length);

      if (mappedPosts.length > 0) {
        setDiscoverPosts((prev) => {
          const base = Array.isArray(prev) ? [...prev] : [];
          const existingIds = new Set(base.map((item) => item.id));
          mappedPosts.forEach((item) => {
            if (!existingIds.has(item.id)) {
              base.push(item);
              existingIds.add(item.id);
            }
          });
          return base;
        });
      }
    } catch (error) {
      logger.error("Error loading more discover posts:", error);
      toast.error("Failed to load more posts");
      setDiscoverViewerError("load_more_failed");
    } finally {
      setDiscoverViewerLoading(false);
    }
  }, [
    discoverViewerFilters,
    discoverViewerHasMore,
    discoverViewerLoading,
    discoverViewerOffset,
    discoverViewerQuery,
    discoverViewerSource,
    mapSearchResultToPost,
    showDiscoverViewer,
  ]);

  useEffect(() => {
    if (!showDiscoverViewer) {
      return;
    }

    const container = discoverViewerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      if (!discoverViewerHasMore || discoverViewerLoading) {
        return;
      }

      const threshold = 600;
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - threshold
      ) {
        loadMoreDiscoverViewerPosts();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [
    discoverViewerHasMore,
    discoverViewerLoading,
    loadMoreDiscoverViewerPosts,
    showDiscoverViewer,
  ]);

  const runDiscoverSearch = useCallback(
    async (query, filtersState) => {
      try {
        const response = await discoverAPI.search({
          query,
          limit: 50,
          offset: 0,
          instances:
            filtersState.instance === "all" ? [] : [filtersState.instance],
          slideTypes:
            filtersState.slideType === "all" ? [] : [filtersState.slideType],
          languages:
            filtersState.language === "all" ? [] : [filtersState.language],
          difficulties:
            filtersState.difficulty === "all" ? [] : [filtersState.difficulty],
        });

        const results = response?.results || [];
        // Map results to post format for grid display
        const mappedPosts = results.map(mapSearchResultToPost);
        setSearchResults(mappedPosts);
      } catch (error) {
        logger.error("Discover search error:", error);
        toast.error("Search failed");
        setSearchResults([]);
      }
    },
    [toast, mapSearchResultToPost]
  );

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      await runDiscoverSearch(query, discoverFilters);
    } else {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    if (searchQuery.trim()) {
      runDiscoverSearch(searchQuery, discoverFilters);
    }
  }, [discoverFilters, searchQuery, runDiscoverSearch]);

  // Close discover viewer when leaving tab to avoid stale overlays
  useEffect(() => {
    if (currentTab !== "search") {
      setShowDiscoverViewer(false);
      setDiscoverScrollTargetId(null);
      setDiscoverPosts([]);
    }
  }, [currentTab]);

  // Keep viewer filters in sync with current discover filters
  useEffect(() => {
    setDiscoverViewerFilters(discoverFilters);
    setShowDiscoverViewer(false);
  }, [discoverFilters]);

  const closeDiscoverViewer = useCallback(() => {
    setShowDiscoverViewer(false);
    setDiscoverScrollTargetId(null);
    setDiscoverViewerLoading(false);
    setDiscoverViewerHasMore(false);
    setDiscoverViewerOffset(0);
    setDiscoverViewerError(null);
  }, []);

  const handleProfileClick = (entity) => {
    setSelectedProfile(entity);
    setCurrentTab("profile");
  };
  const handleProfileClickChapter = (entity) => {
    setSelectedProfile(entity);
   
  };

  const handleStoryClick = (entity, entityStories) => {
    setCurrentStoryEntity(entity);
    setCurrentStoryData(entityStories);
    setShowStoryViewer(true);
  };

  const handleStoryViewed = (storyId) => {
    if (!viewedStories.includes(storyId)) {
      setViewedStories((prev) => [...prev, storyId]);
    }
  };

  const handleCloseStory = () => {
    setShowStoryViewer(false);
    setCurrentStoryEntity(null);
    setCurrentStoryData([]);
  };

  // Create stories data in the format expected by StoriesBar
  const storiesData = stories
    ? stories.map((story) => ({
        ...story,
        viewed_by_user: false, // TODO: Track viewed stories
      }))
    : [];

  // Don't show loading screen forever - after initial load, proceed even with no instances
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    // Mark initial load as complete after a short timeout
    const timer = setTimeout(() => setInitialLoadComplete(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (instances.length === 0 && !initialLoadComplete) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-24 mx-auto"></div>
          </div>
          <p className="text-gray-600 mt-4">Loading your study instances...</p>
        </div>
      </div>
    );
  }

  // Remove this check - let the app render with null checks in place

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-500">
      {/* Phase 3: Simplified Header - Profile-First Instance Management */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40">
        <Header
          setShowSettings={setShowSettings}
          setCurrentTab={setCurrentTab}
        />
      </div>

      {/* Main Content - with bottom padding for footer + safe area */}
      <div
        className="max-w-md mx-auto bg-white dark:bg-slate-900 min-h-screen shadow-2xl dark:shadow-none transition-colors duration-500"
        style={{ paddingBottom: 'var(--content-bottom-padding, 64px)' }}
      >
        <Tabs
          value={currentTab}
          onValueChange={setCurrentTab}
          className="w-full"
        >
          <TabsContent value="feed" className="mt-0">
            {currentInstance?.id ? (
              <EnhancedFeed
                setShowSettings={setShowSettings}
                setCurrentTab={setCurrentTab}
                instanceId={currentInstance?.id}
                onProfileClick={handleProfileClick}
                onStoryClick={handleStoryClick}
                onOpenSaveModal={handleOpenSaveModal}
                savedPostIds={savedPosts.map((p) => p.id)}
                stories={stories || []}
                entities={entities || []}
              />
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-gray-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('feed.no_content_title')}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {t('feed.no_content_desc')}
                  </p>
                  <Button
                    onClick={handleNewUploadClick}
                    disabled={limitLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {limitLoading ? t('common.loading') : t('feed.upload_button')}
                  </Button>
                  <p className="text-xs text-gray-500 mt-4">
                    {t('feed.go_to_profile')}
                  </p>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="search" className="mt-0">
            <div className="p-4 space-y-4">
              {currentInstance?.id ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder={t('search.placeholder')}
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => {
                        const query = e.target.value;
                        setSearchQuery(query);
                        handleSearch(query); // Trigger search on input change for live results
                      }}
                    />
                  </div>

                  {searchQuery ? (
                    /* Search Results - Grid View (same as Discover) */
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900">
                        {t('search.results_title')}
                      </h3>
                      {filterControls}
                      {/* Use DiscoverGrid for search results */}
                      <DiscoverGrid
                        filters={discoverFilters}
                        searchResults={searchResults}
                        onPostClick={handleDiscoverGridPostClick}
                        viewedPosts={[]}
                      />
                    </div>
                  ) : (
                    /* Discover Tab - Grid View */
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        {t('search.discover_title')}
                      </h3>
                      {filterControls}
                      <DiscoverGrid
                        filters={discoverFilters}
                        onPostClick={handleDiscoverGridPostClick}
                        viewedPosts={[]}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Search className="w-10 h-10 text-gray-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('search.no_content_title')}
                    </h2>
                    <p className="text-gray-600 mb-6">
                      {t('search.no_content_desc')}
                    </p>
                    <Button
                      onClick={() => setCurrentTab("profile")}
                      variant="outline"
                    >
                      {t('search.go_to_profile')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="saved" className="mt-0">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {!showCollections && currentCollection && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCurrentCollection(null);
                        loadSavedPosts(null);
                      }}
                    >
                      ← {t('saved.all_posts')}
                    </Button>
                  )}
                  <h2 className="text-xl font-bold text-gray-900">
                    {showCollections
                      ? t('saved.collections')
                      : currentCollection || t('saved.title')}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  {!showCollections && (
                    <>
                      <Button
                        variant={
                          savedPostsViewMode === "grid" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSavedPostsViewMode("grid")}
                      >
                        {t('saved.grid_view')}
                      </Button>
                      <Button
                        variant={
                          savedPostsViewMode === "list" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSavedPostsViewMode("list")}
                      >
                        {t('saved.list_view')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant={showCollections ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowCollections(!showCollections)}
                  >
                    {showCollections ? t('saved.posts') : t('saved.collections')}
                  </Button>
                </div>
              </div>

              {showCollections ? (
                <div className="space-y-4">
                  <div
                    className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={async () => {
                      const name = prompt(t('saved.create_collection_title'));
                      if (name && name.trim()) {
                        await handleCreateCollection(name.trim());
                      }
                    }}
                  >
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Plus className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {t('saved.create_collection_title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('saved.create_collection_desc')}
                      </p>
                    </div>
                  </div>

                  {savedCollections.map((collection) => (
                    <div
                      key={collection.name}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors flex items-center justify-between"
                    >
                      {logger.log("Rendering collection:", collection)}
                      <div
                        className="flex items-center gap-4 cursor-pointer"
                        onClick={() => {
                          setCurrentCollection(collection.name);
                          setShowCollections(false);
                          loadSavedPosts(collection.name);
                        }}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                          {collection.cover_media ? (
                            <img
                              src={collection.cover_media}
                              alt={collection.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Bookmark className="w-8 h-8 text-gray-400" />
                          )}
                        </div>
                        <div>
                          {logger.log("Rendering collection:", collection)}
                          <h3 className="font-semibold text-gray-900">
                            {collection.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {collection.count || 0} posts
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleRenameCollection(collection.name)
                          }
                        >
                          {t('common.rename')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() =>
                            handleDeleteCollection(collection.name)
                          }
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : /* Saved Posts View */
              savedPosts.length > 0 ? (
                savedPostsViewMode === "grid" ? (
                  <div className="grid grid-cols-3 gap-1">
                    {savedPosts.map((post) => (
                      <div
                        key={post.id}
                        className="aspect-square bg-gray-100 relative cursor-pointer hover:opacity-80 transition-opacity group"
                        onClick={() => {
                          setSavedPostsViewMode("list");
                          setSavedScrollTargetId(post.id);
                        }}
                      >
                        {post.media_url ? (
                          <img
                            src={post.media_url}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                            <span className="text-xs text-gray-700 text-center px-2 font-medium">
                              {post.title?.substring(0, 15)}...
                            </span>
                          </div>
                        )}
                        {post.slides && post.slides.length > 1 && (
                          <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                            {post.slides.length} slides
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                          <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity text-center">
                            <div className="flex items-center justify-center gap-3 mb-1">
                              <span>Likes {post.likes || 0}</span>
                              <span>Comments {post.comments || 0}</span>
                            </div>
                            <div className="text-xs text-white text-opacity-80">
                              {post.entity?.name}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedPosts.map((post) => (
                      <div key={post.id} data-post-id={post.id}>
                        <InstagramCarousel
                          setShowSettings={setShowSettings}
                          setCurrentTab={setCurrentTab}
                          post={post}
                          entity={
                            post.entity ||
                            entities?.find((e) => e.id === post.entity_id)
                          }
                          onLike={(postId, liked) => {
                            setSavedPosts((prev) =>
                              prev.map((p) =>
                                p.id === postId
                                  ? {
                                      ...p,
                                      liked_by_user: liked,
                                      likes: liked
                                        ? (p.likes || 0) + 1
                                        : Math.max(0, (p.likes || 0) - 1),
                                    }
                                  : p
                              )
                            );
                          }}
                          onSave={async (postId, saved) => {
                            const postToOpen = savedPosts.find(
                              (p) => p.id === postId
                            );
                            if (!postToOpen) return;

                            if (saved) {
                              handleOpenSaveModal(postToOpen);
                            } else {
                              try {
                                await interactionsAPI.removeFromAllCollections(
                                  postId
                                );
                                setSavedPosts((prev) =>
                                  prev.filter((p) => p.id !== postId)
                                );
                                await loadCollections();
                                toast.success("Removed from saved");
                              } catch (error) {
                                logger.error(
                                  "Error removing from saved:",
                                  error
                                );
                                toast.error("Failed to remove from saved");
                              }
                            }
                          }}
                          onComment={() => {
                            toast.info("Comment feature coming soon!");
                          }}
                          onProfileClick={handleProfileClick}
                        />
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('saved.no_posts_title')}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t('saved.no_posts_desc')}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="chapters" className="mt-0">
            {selectedProfile ? (
              <ProfilePage
                entity={selectedProfile}
                instanceId={currentInstance?.id}
                currentUser={currentUser}
                onBack={() => {
                  setSelectedProfile(null);
                  setCurrentTab("chapters");
                }}
              />
            ) : (
              <ChaptersTab
                instanceId={currentInstance?.id}
                onChapterClick={handleProfileClickChapter}
              />
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-0">
            {showSettings ? (
              <SettingsScreen
                onBack={() => setShowSettings(false)}
                currentUser={{ name: "Demo User", email: "demo@scrolled.app" }}
              />
            ) : (
              /* New Instagram-Style User Profile */
              <UserProfileNew
                onBack={() => setCurrentTab("feed")}
                onNewInstance={handleNewUploadClick}
                setShowSettings={setShowSettings}
                setCurrentTab={setCurrentTab}
                onInstanceSelect={(selectedInstance) => {
                  logger.log("🎯 Instance selected:", selectedInstance);

                  // Update Zustand store (no need to update MainLayout state)
                  useInstanceStore
                    .getState()
                    .setSelectedInstanceId(selectedInstance.id);

                  // Switch to feed tab to show the content
                  setCurrentTab("feed");

                  // Show success message
                  logger.log(
                    `✅ Switched to instance: ${selectedInstance.name}`
                  );
                }}
              />
            )}
          </TabsContent>

          {/* Bottom Navigation - positioned above Android nav bar safe area */}
          <TabsList
            className="fixed left-1/2 transform -translate-x-1/2 w-full max-w-md h-16 bg-white border-t border-gray-200 rounded-none z-50"
            style={{ bottom: 'var(--safe-area-bottom, 0px)' }}
          >
            <TabsTrigger
              value="feed"
              className="flex-1 flex flex-col items-center gap-1 data-[state=active]:text-blue-600"
            >
              <Home className="w-5 h-5" />
              <span className="text-xs">{t('navigation.feed')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="search"
              className="flex-1 flex flex-col items-center gap-1 data-[state=active]:text-blue-600"
            >
              <Search className="w-5 h-5" />
              <span className="text-xs">{t('navigation.search')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="chapters"
              className="flex-1 flex flex-col items-center gap-1 data-[state=active]:text-blue-600"
            >
              <BookOpen className="w-5 h-5" />
              <span className="text-xs">{t('navigation.chapters')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="saved"
              className="flex-1 flex flex-col items-center gap-1 data-[state=active]:text-blue-600"
            >
              <Bookmark className="w-5 h-5" />
              <span className="text-xs">{t('navigation.saved')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="profile"
              className="flex-1 flex flex-col items-center gap-1 data-[state=active]:text-blue-600"
            >
              <User className="w-5 h-5" />
              <span className="text-xs">{t('navigation.profile')}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Story Viewer Modal */}
      {showStoryViewer && currentStoryEntity && (
        <StoryViewer
          entity={currentStoryEntity}
          stories={currentStoryData}
          onClose={handleCloseStory}
          onProfileClick={handleProfileClick}
          onStoryViewed={handleStoryViewed}
        />
      )}

      {/* Save to Collection Modal */}
      <SaveToCollectionModal
        isOpen={showSaveToCollection}
        post={postToSave}
        collections={savedCollections}
        selectedCollections={postCurrentCollections}
        onConfirm={handleSaveToCollections}
        onCancel={() => {
          setShowSaveToCollection(false);
          setPostToSave(null);
          setPostCurrentCollections([]);
        }}
        onCreateCollection={handleCreateCollection}
        onRemoveAll={handleRemoveFromAllCollections}
      />

      {/* Upload Limit Check Modal - shown before upload modal */}
      <UploadLimitModal
        isOpen={showLimitModal}
        onClose={handleCloseLimitModal}
        onProceed={handleProceedToUpload}
        limitStatus={limitStatus}
      />

      {/* PDF Upload Modal */}
      <PDFUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Discover/Search Fullscreen Viewer */}
      {showDiscoverViewer && (
        <div className="fixed inset-0 bg-white z-[60] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <button
              onClick={closeDiscoverViewer}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h2 className="text-sm font-semibold text-gray-900">Discover</h2>
            <div className="w-5" />
          </div>

          <div
            ref={discoverViewerRef}
            className="flex-1 overflow-y-auto bg-gray-50"
          >
            <div className="space-y-4 p-4 pb-10">
              {discoverPosts.map((post) => (
                <div key={post.id} data-post-id={post.id}>
                  <InstagramCarousel
                    setShowSettings={setShowSettings}
                    setCurrentTab={setCurrentTab}
                    post={post}
                    entity={
                      entities?.find((e) => e.id === post.entity_id) || {
                        id: post.entity_id || "default",
                        name: post.entity_name || "Content",
                        avatar_url: post.entity_avatar_url || "",
                        verified: false,
                      }
                    }
                    onLike={async (postId, liked) => {
                      try {
                        await interactionsAPI.toggleLike(postId, liked);
                        setDiscoverPosts((prev) =>
                          prev.map((p) =>
                            p.id === postId
                              ? {
                                  ...p,
                                  liked_by_user: liked,
                                  likes: liked
                                    ? (p.likes || 0) + 1
                                    : Math.max(0, (p.likes || 0) - 1),
                                }
                              : p
                          )
                        );
                      } catch (error) {
                        logger.error("Error toggling like:", error);
                        toast.error("Failed to update like");
                      }
                    }}
                    onSave={async (postId, saved) => {
                      const postToOpen = discoverPosts.find(
                        (p) => p.id === postId
                      );
                      if (!postToOpen) return;

                      if (saved) {
                        handleOpenSaveModal(postToOpen);
                      } else {
                        try {
                          await interactionsAPI.removeFromAllCollections(
                            postId
                          );
                          setDiscoverPosts((prev) =>
                            prev.map((p) =>
                              p.id === postId
                                ? {
                                    ...p,
                                    saved_by_user: false,
                                    collections: [],
                                  }
                                : p
                            )
                          );
                          toast.success("Removed from saved");
                        } catch (error) {
                          logger.error("Error removing from saved:", error);
                          toast.error("Failed to remove from saved");
                        }
                      }
                    }}
                    onComment={() => {
                      toast.info("Comment feature coming soon!");
                    }}
                    onProfileClick={handleProfileClick}
                    onPostDeleted={(postId) => {
                      setDiscoverPosts((prev) =>
                        prev.filter((p) => p.id !== postId)
                      );
                      toast.success("Post deleted");
                    }}
                  />
                </div>
              ))}

              {discoverViewerLoading && (
                <div className="flex justify-center py-6">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
                </div>
              )}

              {discoverViewerError && (
                <div className="flex flex-col items-center gap-2 py-6">
                  <p className="text-sm text-gray-600">
                    Failed to load more posts.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadMoreDiscoverViewerPosts}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {!discoverViewerLoading &&
                !discoverViewerHasMore &&
                discoverPosts.length > 0 && (
                  <div className="text-center py-6 text-xs text-gray-500">
                    You're all caught up
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Modern Instagram-Style Post Viewer Modal for Discover/Search Posts */}
      {selectedPost && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center"
          onClick={() => setSelectedPost(null)}
        >
          <div
            className="w-full max-w-md mx-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedPost(null)}
              className="absolute -top-12 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Instagram Carousel Component */}
            <div className="bg-white rounded-lg overflow-hidden max-h-[90vh] overflow-y-auto">
              <InstagramCarousel
                setShowSettings={setShowSettings}
                setCurrentTab={setCurrentTab}
                post={selectedPost}
                entity={
                  entities?.find((e) => e.id === selectedPost.entity_id) || {
                    id: selectedPost.entity_id || "default",
                    name: selectedPost.entity_name || "Content",
                    avatar_url: selectedPost.entity_avatar_url || "",
                    verified: false,
                  }
                }
                onLike={(postId, liked) => {
                  setSelectedPost((prev) =>
                    prev
                      ? {
                          ...prev,
                          liked_by_user: liked,
                          likes: liked
                            ? (prev.likes || 0) + 1
                            : Math.max(0, (prev.likes || 0) - 1),
                        }
                      : null
                  );
                }}
                onSave={async (postId, saved) => {
                  if (saved) {
                    await handleOpenSaveModal(selectedPost);
                  } else {
                    try {
                      await interactionsAPI.removeFromAllCollections(postId);
                      setSelectedPost((prev) =>
                        prev
                          ? { ...prev, saved_by_user: false, collections: [] }
                          : null
                      );
                      toast.success("Removed from saved");
                    } catch (error) {
                      logger.error("Error removing from saved:", error);
                      toast.error("Failed to remove from saved");
                    }
                  }
                }}
                onComment={(postId) => {
                  toast.info("Comment feature coming soon!");
                }}
                onProfileClick={handleProfileClick}
                onPostDeleted={(postId) => {
                  setSelectedPost(null);
                  toast.success("Post deleted");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
